import React, { useRef, useState, useEffect } from 'react'
import { Toast } from 'react-vant'
import { APIWeb } from '../../api'
import { useTranslation } from 'react-i18next'

export interface UploadFileProps {
  /** 是否禁用 */
  disabled?: boolean
  /** 文件选择后的回调 */
  onChange?: (file: File | null) => void
  /** 当前文件 */
  value?: File | null
  /** 自定义样式类名 */
  className?: string
  /** 自定义样式 */
  style?: React.CSSProperties
  /** 子元素 */
  children?: React.ReactNode
  /** 最大文件大小（MB），默认5MB */
  maxSize?: number
  /** 文件大小超出限制时的回调 */
  onSizeExceed?: (file: File, maxSize: number) => void
  /** 文件类型不符合时的回调 */
  onTypeError?: (file: File) => void
  /** 是否自动上传，默认为 false */
  autoUpload?: boolean
  /** 上传成功回调，返回文件 URL */
  onUploadSuccess?: (fileUrl: string, file: File) => void
  /** 上传失败回调 */
  onUploadError?: (error: string, file: File) => void
  /** 上传开始回调 */
  onUploadStart?: (file: File) => void
  /** 上传进度变化回调 */
  onUploadProgress?: (progress: number, file: File) => void
  /** 占位背景图片URL */
  placeholder?: string
  /** 已上传的图片URL（用于显示已上传图片的预览） */
  uploadedUrl?: string
  /** 删除按钮图标URL（可选，默认使用内置图标） */
  removeIcon?: string
  /** 上传按钮图标URL（可选） */
  uploadIcon?: string
  /** 上传按钮文字（可选，默认"上传"） */
  uploadText?: string
  /** 是否显示上传提示文字 */
  showUploadTip?: boolean
  /** 是否显示删除按钮，默认为 true */
  showRemoveButton?: boolean
  /** 删除图片时的回调（用于清除已上传的URL） */
  onRemove?: () => void
}

const UploadFile: React.FC<UploadFileProps> = ({
    disabled = false,
    onChange,
    value,
    className = '',
    style,
    children,
    maxSize = 5, // 默认5MB
    onSizeExceed,
    onTypeError,
    autoUpload = false,
    onUploadSuccess,
    onUploadError,
    onUploadStart,
    onUploadProgress,
    placeholder,
    uploadedUrl,
    removeIcon='/images/my/icons/remove.svg',
    uploadIcon='/images/my/icons/camera.svg',
    uploadText,
    showUploadTip = true,
    showRemoveButton = true,
    onRemove,
}) => {
    const { t } = useTranslation()
    const fileInputRef = useRef<HTMLInputElement>(null)
    const [isDragging, setIsDragging] = useState(false)
    const [previewUrl, setPreviewUrl] = useState<string | null>(null)
    const [uploading, setUploading] = useState(false)
    const [uploadProgress, setUploadProgress] = useState(0)

    // 支持的图片类型
    const acceptedImageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp']
    const accept = acceptedImageTypes.join(',')

    // 真实的 S3 上传函数
    const uploadToS3 = async (file: File): Promise<string> => {
        try {
            setUploading(true)
            setUploadProgress(0)
            
            // 通知开始上传
            onUploadProgress?.(0, file)
            
            // 模拟线性进度增长
            const progressInterval = setInterval(() => {
                setUploadProgress(prev => {
                    const newProgress = Math.min(prev + 10, 90) // 最多到90%，等待请求完成
                    onUploadProgress?.(newProgress, file)
                    return newProgress
                })
            }, 50) // 每50ms增加2%
            
            try {
                // 1. 获取 S3 上传 URL
                const s3Response = await APIWeb.s3uploadFile({
                    content_type: file.type
                })
                
                if (!s3Response?.upload_url || !s3Response?.file_url) {
                    clearInterval(progressInterval)
                    throw new Error('上传失败，未获取到上传地址')
                }
                
                // 2. 使用 PUT 方法上传文件到 S3 
                const uploadResponse = await fetch(s3Response.upload_url, {
                    method: 'PUT',
                    body: file,
                    headers: {
                        'Content-Type': file.type,
                    },
                })
                console.log('fetch失败', uploadResponse)
                // 清除进度定时器
                clearInterval(progressInterval)
                
                if (!uploadResponse.ok) {
                    throw new Error(`S3上传失败: ${uploadResponse.status}`)
                }
                
                // 上传成功，设置为100%
                setUploadProgress(100)
                onUploadProgress?.(100, file)
                
                return s3Response.file_url
                
            } catch (apiError) {
                // 清除进度定时器
                clearInterval(progressInterval)
                throw apiError
            }
            
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : '上传失败'
            throw new Error(errorMessage)
        } finally {
            setUploading(false)
        }
    }

    // 将MB转换为字节
    const maxSizeInBytes = maxSize * 1024 * 1024

    // 验证文件是否为图片
    const isValidImageType = (file: File): boolean => {
        return acceptedImageTypes.includes(file.type)
    }

    // 处理文件选择和上传
    const handleFileSelect = async (file: File) => {
        // 验证文件类型
        if (!isValidImageType(file)) {
            onTypeError?.(file)
            return
        }

        // 验证文件大小
        if (file.size > maxSizeInBytes) {
            if (onSizeExceed) {
                onSizeExceed(file, maxSize)
            } else {
                Toast.fail(t('图片大小需小于maxSizeM', { maxSize }))
            }
            return
        }

        // 更新文件状态
        onChange?.(file)

        // 如果启用自动上传，则开始上传
        if (autoUpload) {
            try {
                // 上传开始回调
                onUploadStart?.(file)
                
                const fileUrl = await uploadToS3(file)
                
                // 上传成功回调
                onUploadSuccess?.(fileUrl, file)
                
            } catch (error) {
                // 上传失败时重置进度条
                setUploadProgress(0)
                
                const errorMessage = error instanceof Error ? error.message : '上传失败'
                onUploadError?.(errorMessage, file)
                
                // 上传失败时清除文件
                onChange?.(null)
            }
        }
    }

    // 生成图片预览URL
    useEffect(() => {
        if (value && isValidImageType(value)) {
            const url = URL.createObjectURL(value)
            setPreviewUrl(url)
            return () => URL.revokeObjectURL(url)
        } else {
            setPreviewUrl(null)
        }
    }, [value])

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0]
        if (file) {
            handleFileSelect(file)
        }
        // 清空input值，允许重复选择同一文件
        event.target.value = ''
    }

    const handleClick = () => {
        if (!disabled && !uploading) {
            fileInputRef.current?.click()
        }
    }

    const handleDragOver = (event: React.DragEvent) => {
        event.preventDefault()
        if (!disabled && !uploading) {
            setIsDragging(true)
        }
    }

    const handleDragLeave = (event: React.DragEvent) => {
        event.preventDefault()
        setIsDragging(false)
    }

    const handleDrop = (event: React.DragEvent) => {
        event.preventDefault()
        setIsDragging(false)

        if (disabled || uploading) return

        const files = event.dataTransfer.files
        if (files.length > 0) {
            const file = files[0]
            handleFileSelect(file)
        }
    }

    const handleRemove = (event: React.MouseEvent) => {
        event.stopPropagation()
        onChange?.(null)
        onRemove?.()
    }

    // 获取实际显示的预览URL（优先使用本地预览，其次使用已上传的URL）
    const displayPreviewUrl = previewUrl || uploadedUrl

    // 如果有图片预览，显示预览
    if (displayPreviewUrl) {
        return (
            <div
                className={`
                    relative cursor-pointer rounded-2xl transition-colors overflow-hidden
                    bg-[#F2F4F7] border border-solid border-[#EAECF0]
                    ${isDragging ? 'border-blue-400 bg-blue-50' : ''}
                    ${disabled || uploading ? 'cursor-not-allowed' : ''}
                    ${className}
                `}
                style={style}
                onClick={handleClick}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
            >
                <input
                    ref={fileInputRef}
                    type="file"
                    accept={accept}
                    onChange={handleInputChange}
                    className="hidden"
                    disabled={disabled || uploading}
                />

                {/* 图片预览 */}
                <div className="relative w-full h-full">
                    <img
                        src={displayPreviewUrl}
                        alt={t('预览图片')}
                        className="w-full h-full object-cover"
                    />

                    {/* 删除按钮 */}
                    {showRemoveButton && (
                        <button
                            onClick={(e) => {
                                e.preventDefault()
                                e.stopPropagation()
                                handleRemove(e)
                            }}
                            className="absolute bottom-3 right-3 w-10 h-10 rounded-full bg-[#D7D9DE] flex items-center justify-center border-none cursor-pointer hover:opacity-80 transition-opacity"
                        >
                            {removeIcon ? (
                                <img src={removeIcon} alt="remove" />
                            ) : (
                                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M3 5H17M8 5V3C8 2.44772 8.44772 2 9 2H11C11.5523 2 12 2.44772 12 3V5M15 5V17C15 17.5523 14.5523 18 14 18H6C5.44772 18 5 17.5523 5 17V5" stroke="#86909C" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            )}
                        </button>
                    )}
                
                    {/* 上传进度条 */}
                    {uploading && (
                        <div className="absolute inset-0 bg-black/50 flex flex-col items-center justify-center">
                            <div className="w-3/4 bg-gray-300 rounded-full h-2 mb-2">
                                <div 
                                    className="bg-[var(--primary-color)] h-2 rounded-full transition-all duration-300"
                                    style={{ width: `${Math.round(uploadProgress)}%` }}
                                ></div>
                            </div>
                            <div className="text-white text-sm">
                                {t('上传中')} {Math.round(uploadProgress)}%
                            </div>
                        </div>
                    )}
                </div>
            </div>
        )
    }

    // 如果有 children，使用自定义内容
    if (children) {
        return (
            <div
                className={`
                    relative cursor-pointer border-2 border-dashed rounded-lg transition-colors
                    ${isDragging ? 'border-blue-400 bg-blue-50' : 'border-gray-300'}
                    ${disabled || uploading ? 'opacity-50 cursor-not-allowed' : 'hover:border-gray-400'}
                    ${className}
                `}
                style={style}
                onClick={handleClick}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
            >
                <input
                    ref={fileInputRef}
                    type="file"
                    accept={accept}
                    onChange={handleInputChange}
                    className="hidden"
                    disabled={disabled || uploading}
                />
                {children}
            </div>
        )
    }

    // 默认上传区域（支持占位图）
    return (
        <div
            onClick={handleClick}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            className={`
                relative w-full rounded-2xl overflow-hidden
                bg-[#F4F6F9] 
                flex flex-col items-center justify-center cursor-pointer
                transition-colors hover:bg-[#F2F4F7]
                ${isDragging ? 'bg-blue-50' : ''}
                ${disabled || uploading ? 'opacity-50 cursor-not-allowed' : ''}
                ${className}
            `}
            style={style}
        >
            <input
                ref={fileInputRef}
                type="file"
                accept={accept}
                onChange={handleInputChange}
                className="hidden"
                disabled={disabled || uploading}
            />

            {/* 占位背景图 */}
            {placeholder && (
                <img
                    src={placeholder}
                    alt=""
                    className="absolute inset-0 w-full h-full object-contain pointer-events-none"
                />
            )}

            {/* 上传按钮 */}
            <div className="relative z-10 flex flex-col items-center">
                <div className="px-5 py-2.5 bg-[#1D2129] rounded-lg flex items-center gap-2">
                    <img src={uploadIcon} alt="upload" />
                    <span className="text-white text-sm">{uploadText || t('上传')}</span>
                </div>
                {showUploadTip && !placeholder && (
                    <p className="text-sm text-[#86909C] mt-3">{t('点击按钮进行上传')}</p>
                )}
            </div>

            {/* 文件大小提示 */}
            <p className="absolute bottom-1 left-0 right-0 text-center text-xs text-[#C0C4CC] z-10">{t('图片大小需小于maxSizeM', { maxSize })}</p>
        </div>
    )
}

export default UploadFile
